import {
  child,
  get,
  getDatabase,
  onValue,
  push,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import { firebaseApp } from "./firebase";
import type { WorkoutPlanDto } from "./types";

/**
 * Everything the portal reads and writes, on the paths the Android app defines.
 *
 * There is no server in between: the portal acts as the signed-in user and the database rules
 * decide what that is allowed to touch. A coach can write an athlete's plans only while
 * `users/{athlete}/coaches/{coach}` exists, which only the athlete can create.
 */
export const db = getDatabase(firebaseApp);

export interface AthleteSummary {
  uid: string;
  username: string;
}

export interface CoachProfile {
  coachCode: string;
  username: string;
}

export function watchCoachProfile(
  coachUid: string,
  onChange: (profile: CoachProfile) => void,
): () => void {
  return onValue(ref(db, `users/${coachUid}/profile`), (snapshot) => {
    const value = snapshot.val() as Partial<CoachProfile> | null;
    onChange({ coachCode: value?.coachCode ?? "", username: value?.username ?? "" });
  });
}

/**
 * Claims a code for this coach, releasing the previous one.
 *
 * Codes are a mutable handle: athletes are linked by uid, so renaming breaks nothing. The write
 * fails if someone else already holds the code — the rules only allow claiming a free one.
 */
export async function setCoachCode(
  coachUid: string,
  nextCode: string,
  previousCode: string,
): Promise<void> {
  const code = nextCode.trim();
  if (!code) throw new Error("Choose a code");
  if (code === previousCode) return;

  const taken = await get(child(ref(db, "coachCodes"), code));
  if (taken.exists() && taken.val() !== coachUid) {
    throw new Error("That code is already taken");
  }

  await set(ref(db, `coachCodes/${code}`), coachUid);
  await set(ref(db, `users/${coachUid}/profile/coachCode`), code);
  if (previousCode && previousCode !== code) {
    await remove(ref(db, `coachCodes/${previousCode}`));
  }
}

/** The athletes who have authorised this coach, with whatever name they go by. */
export function watchAthletes(
  coachUid: string,
  onChange: (athletes: AthleteSummary[]) => void,
): () => void {
  return onValue(ref(db, `coachAthletes/${coachUid}`), async (snapshot) => {
    const uids = Object.keys((snapshot.val() as Record<string, boolean> | null) ?? {});
    const summaries = await Promise.all(
      uids.map(async (uid) => {
        // A name we cannot read is not a reason to hide the athlete from their own coach.
        const profile = await get(ref(db, `users/${uid}/profile`)).catch(() => null);
        const username = (profile?.val() as { username?: string } | null)?.username ?? "";
        return { uid, username: username || uid.slice(0, 8) };
      }),
    );
    summaries.sort((a, b) => a.username.localeCompare(b.username));
    onChange(summaries);
  });
}

export function watchAthletePlans(
  athleteUid: string,
  onChange: (plans: WorkoutPlanDto[]) => void,
): () => void {
  return onValue(ref(db, `users/${athleteUid}/workoutPlans`), (snapshot) => {
    const value = (snapshot.val() as Record<string, WorkoutPlanDto> | null) ?? {};
    const plans = Object.entries(value).map(([id, plan]) => ({ ...plan, id: plan.id || id }));
    plans.sort((a, b) => (b.createdAtMillis ?? 0) - (a.createdAtMillis ?? 0));
    onChange(plans);
  });
}

/**
 * Writes a plan into an athlete's list. Never touches `activeWorkoutPlanId`: which plan is being
 * trained stays the athlete's decision.
 */
export async function saveAthletePlan(
  athleteUid: string,
  plan: WorkoutPlanDto,
  /** Omitted when the athlete is editing their own plan. */
  authoredByCoachUid?: string,
): Promise<string> {
  const id = plan.id || (push(ref(db, `users/${athleteUid}/workoutPlans`)).key as string);
  const stored: WorkoutPlanDto & { authoredByCoachUid?: string } = {
    ...plan,
    id,
    createdAtMillis: plan.createdAtMillis || Date.now(),
    ...(authoredByCoachUid ? { authoredByCoachUid } : {}),
  };
  await update(ref(db, `users/${athleteUid}/workoutPlans/${id}`), stored);
  return id;
}

export async function deleteAthletePlan(athleteUid: string, planId: string): Promise<void> {
  await remove(ref(db, `users/${athleteUid}/workoutPlans/${planId}`));
}
